import { createSlice } from '@reduxjs/toolkit';

// utils
import axios, { endpoints } from '../../utils/axios'; // import dispatch from store
import { getUsage } from './plan';
import { trackEvent, ANALYTICS_EVENTS } from '../../utils/posthog';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  listings: [],
  archivedListings: [],
  currentListing: null,
};

const slice = createSlice({
  name: 'listings',
  initialState,
  reducers: {
    // START LOADING
    startLoading (state) {
      state.isLoading = true;
    },
    // END LOADING
    endLoading (state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError (state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    // RESET ERROR
    resetError (state, action) {
      state.isLoading = false;
      state.error = null;
    },

    // GET LISTINGS
    getListingsSuccess (state, action) {
      const listings = action.payload;
      state.listings = Array.isArray(listings) ? listings : [];
      state.isLoading = false;
    },

    // GET LISTING
    getListingSuccess (state, action) {
      const listing = action.payload;
      state.currentListing = listing;
      state.isLoading = false;
    },

    // CREATE LISTING
    createListingSuccess (state, action) {
      const listing = action.payload;
      state.currentListing = listing;
      state.isLoading = false;
    },

    // UPDATE LISTING
    updateListingSuccess (state, action) {
      const listing = action.payload;
      state.currentListing = listing;
      state.isLoading = false;
    },

    // DELETE LISTING
    deleteListingSuccess (state, action) {
      const listing = action.payload;
      state.currentListing = listing;
      state.isLoading = false;
    },

    // GET ARCHIVED LISTINGS
    getArchivedListingsSuccess (state, action) {
      const listings = action.payload;
      state.archivedListings = listings;
      state.isLoading = false;
    },

    // CLEAR CURRENT LISTING
    clearCurrentListing (state) {
      state.currentListing = null;
    }
  },
});

// Reducer
export default slice.reducer;

/**
 * The marketplace a listing's placement belongs to, for per-account actions that are
 * addressed by integrationId. Analytics reports the platform, never the account id.
 */
function platformOfAccount (listing, integrationId) {
  return (listing?.platformAccounts || [])
    .find((pa) => Number(pa.integrationId) === Number(integrationId))?.platform;
}


// ----------------------------------------------------------------------

export function getListings (forceRefresh = false) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const url = endpoints.listing.list;
      // Add cache-busting timestamp parameter if forceRefresh is true
      const finalUrl = forceRefresh ? `${url}?_t=${Date.now()}` : url;
      const response = await axios.get(finalUrl);
      dispatch(slice.actions.getListingsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

/**
 * Re-read the seller's listings after something changed them, together with the plan usage that
 * is counted from those listings — so the usage card in the sidebar keeps matching the inventory
 * without the seller reloading the page. Use this after anything that adds or removes listings.
 *
 * Settled rather than all: getListings rethrows on failure while getUsage handles its own errors,
 * and every caller here fires this without awaiting it.
 */
export function reloadListingsAfterChange () {
  return async (dispatch) => {
    await Promise.allSettled([dispatch(getListings(true)), dispatch(getUsage())]);
  };
}

// ----------------------------------------------------------------------
export function getListing (listingId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const URL = `${endpoints.listing.details}/${listingId}`;

      const response = await axios.get(URL);
      dispatch(slice.actions.getListingSuccess(response.data));
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      dispatch(slice.actions.hasError(message));
    }
  };
}

// ----------------------------------------------------------------------

export function createListing (listing) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios({
        method: 'post',
        url: endpoints.listing.create,
        data: listing,
        headers: {
          'Content-Type': 'multipart/form-data' // This line is often unnecessary as Axios and browsers handle it automatically
        }
      });

      dispatch(slice.actions.createListingSuccess(response.data));
      trackEvent(ANALYTICS_EVENTS.LISTING_CREATE_SUCCEEDED, {
        platformCount: Array.isArray(response.data?.listedOn) ? response.data.listedOn.length : undefined,
        platforms: Array.isArray(response.data?.listedOn) ? response.data.listedOn : undefined,
      });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}


// ----------------------------------------------------------------------

export function updateListing (listing) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {

      const response = await axios({
        method: 'put',
        url: endpoints.listing.update,
        data: listing,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      dispatch(slice.actions.updateListingSuccess(response.data));
      trackEvent(ANALYTICS_EVENTS.LISTING_EDIT_SAVED, {
        platformCount: Array.isArray(response.data?.listedOn) ? response.data.listedOn.length : undefined,
        platforms: Array.isArray(response.data?.listedOn) ? response.data.listedOn : undefined,
      });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));

      throw new Error(error?.response?.data?.message || error.message);
    }
  };
}


// ----------------------------------------------------------------------

export function cancelSchedule (listingId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(endpoints.listing.cancelSchedule(listingId));
      dispatch(slice.actions.updateListingSuccess(response.data));
      trackEvent(ANALYTICS_EVENTS.LISTING_SCHEDULE_CANCELLED);
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}


// ----------------------------------------------------------------------

export function rescheduleListing (listingId, scheduledPublishAt) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(endpoints.listing.reschedule(listingId), { scheduledPublishAt });
      dispatch(slice.actions.updateListingSuccess(response.data));
      trackEvent(ANALYTICS_EVENTS.LISTING_SCHEDULE_RESCHEDULED, {
        // How far ahead the seller moved the publish, in whole hours.
        leadTimeHours: Math.round((new Date(scheduledPublishAt).getTime() - Date.now()) / 3600000),
      });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}


// ----------------------------------------------------------------------

export function archiveListing (ids) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(endpoints.listing.archive, { ids });
      trackEvent(ANALYTICS_EVENTS.LISTING_ARCHIVE_COMPLETED, {
        count: Array.isArray(ids) ? ids.length : 1,
      });

      // Bulk archive returns 202 with batchId
      if (response.status === 202) {
        dispatch(slice.actions.endLoading());
        return response.data; // { message, batchId, total, skipped }
      }

      // Single archive returns 200 with result array
      dispatch(slice.actions.deleteListingSuccess(response.data));
      return response.data;
    } catch (error) {
      // The server's answer is the useful part of a refusal — which items, and why. Thrown as a
      // bare sentence it is something the caller has to pattern-match; carried on the error it is
      // data the dialog can show.
      const body = error?.response?.data;
      const failure = new Error(body?.message || error?.message);
      failure.code = body?.code;
      failure.details = body;
      dispatch(slice.actions.hasError(failure.message));
      throw failure;
    }
  };
}

export function getBulkArchiveStatus (batchId) {
  return async () => {
    try {
      const response = await axios.get(endpoints.listing.archiveStatus(batchId));
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      throw new Error(message);
    }
  };
}

// ----------------------------------------------------------------------

export function unmanageListing (ids) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(endpoints.listing.unmanage, { ids });
      trackEvent(ANALYTICS_EVENTS.LISTING_UNMANAGE, {
        count: Array.isArray(ids) ? ids.length : 1,
      });
      dispatch(slice.actions.endLoading());
      // The rows are gone for good, so the seller has that much of their plan back.
      dispatch(getUsage());
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function bulkCreateListings (listings) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(endpoints.listing.bulkCreate, { listings })
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
/**
 * Start fetching listings from a platform (background job)
 * Returns immediately with job ID
 */
export async function fetchListings (platform, { integrationId, importMode = 'active' } = {}) {
  try {
    const params = new URLSearchParams();
    if (integrationId) params.set('integrationId', integrationId);
    if (importMode) params.set('importMode', importMode);
    const queryStr = params.toString() ? `?${params.toString()}` : '';
    const response = await axios.get(`${endpoints.listing.list}/${platform}${queryStr}`);
    return response.data; // Returns { jobId, status: 'processing' } or { accounts: [...] } if multi-account
  } catch (error) {
    // If backend returns 400 with accounts list, pass it through for account picker
    if (error?.response?.status === 400 && error?.response?.data?.accounts) {
      const err = new Error(error.response.data.message);
      err.accounts = error.response.data.accounts;
      throw err;
    }
    throw new Error(error?.response?.data?.message || error.message);
  }
};

/**
 * Poll for fetch job status
 * Returns the current status and listings when complete
 */
export async function pollFetchStatus (platform = null, integrationId = null) {
  try {
    const params = {};
    if (platform) params.platform = platform;
    if (integrationId) params.integrationId = integrationId;
    const response = await axios.get(`${endpoints.listing.list}/fetch-status`, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Import the listings a seller picked from a staged fetch.
 * @param {string} platform
 * @param {Array<string|number>} selectedIds ids from the staged fetch result
 * @param {string|number|null} integrationId the account the listings were fetched from
 */
export async function importSelectedListings (platform, selectedIds, integrationId = null) {
  try {
    const response = await axios.post(endpoints.listing.importSelected, {
      platform,
      selectedIds,
      integrationId,
    });
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error.message);
  }
};

/**
 * Clear the fetch result after retrieving it
 */
export async function clearFetchResult (platform = null, integrationId = null, { discardStaged = false } = {}) {
  try {
    const body = {};
    if (platform) body.platform = platform;
    if (integrationId) body.integrationId = integrationId;
    if (discardStaged) body.discardStaged = true;
    await axios.post(`${endpoints.listing.list}/fetch-clear`, body);
  } catch (error) {
    console.error('Failed to clear fetch result:', error);
  }
};

/**
 * The seller is done with this import, however it ended — they answered its review, or they walked
 * away from it.
 *
 * The listings an import staged for the seller to choose from are the only thing that says whether
 * that choice is still live: the job's own outcome keeps reading 'needs_selection' for a day
 * afterwards. Leaving them behind is what makes a review the seller already answered open itself
 * again on their next page load, so every ending has to come through here.
 */
export async function finishImport (platform = null, integrationId = null) {
  return clearFetchResult(platform, integrationId, { discardStaged: true });
};


// ----------------------------------------------------------------------
export async function generateListingDetails (images, shortDescription) {

  try {
    const response = await axios.post(endpoints.listing.generate, { images, shortDescription })
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error.message);
  };
}



//----------------------------------------------------------------------
export function resetError () {
  return async (dispatch) => {
    dispatch(slice.actions.resetError());
  };
}

//----------------------------------------------------------------------
export function clearCurrentListing () {
  return async (dispatch) => {
    dispatch(slice.actions.clearCurrentListing());
  };
}

//----------------------------------------------------------------------

// GET ARCHIVED LISTINGS
export function getArchivedListings () {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const url = `${endpoints.listing.archived}`;
      const response = await axios.get(url);
      dispatch(slice.actions.getArchivedListingsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// RESTORE LISTING
export function restoreListing (id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const url = `${endpoints.listing.list}/restore/${id}`;
      await axios.post(url);
      trackEvent(ANALYTICS_EVENTS.LISTING_RESTORE_COMPLETED);
      dispatch(getArchivedListings());
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// PERMANENT DELETE LISTING
export function deleteListing (id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const url = `${endpoints.listing.delete}/${id}`;
      await axios.delete(url);
      trackEvent(ANALYTICS_EVENTS.LISTING_DELETE_COMPLETED);
      dispatch(getArchivedListings());
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// MARK LISTING AS SOLD
export function markListingAsSold (listingId, soldQuantity, soldOnPlatform = null, integrationId = null) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(endpoints.listing.markSold, {
        id: listingId,
        soldQuantity: parseInt(soldQuantity, 10),
        soldOnPlatform,
        integrationId
      });

      dispatch(slice.actions.updateListingSuccess(response.data.listing));
      trackEvent(ANALYTICS_EVENTS.LISTING_MARK_SOLD, { platform: soldOnPlatform || undefined });

      // A sale can take the last marketplace post down, which moves the listing out of the
      // seller's active count.
      dispatch(reloadListingsAfterChange());

      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// DELIST (queued - returns batchId)
// selectedAccounts (optional): [{ platform, integrationId? }] — when provided, only those
// accounts are taken down. Omit/null = every account the items are posted on.
export function delistListings (listingIds, selectedAccounts) {
  return async () => {
    try {
      const body = { ids: listingIds };
      if (Array.isArray(selectedAccounts) && selectedAccounts.length > 0) {
        body.selectedAccounts = selectedAccounts;
      }
      const response = await axios.post(endpoints.listing.delist, body);
      trackEvent(ANALYTICS_EVENTS.LISTING_DELIST_SUBMITTED, {
        count: Array.isArray(listingIds) ? listingIds.length : undefined,
      });
      // Returns { message, batchId, total, browserDelistTargets }
      return response.data;
    } catch (error) {
      // The server's answer is the useful part of a refusal — which limit, how many were picked.
      // Thrown as a bare sentence it is something the caller has to pattern-match; carried on the
      // error it is data the dialog can show.
      const body = error?.response?.data;
      const failure = new Error(body?.message || error?.message);
      failure.code = body?.code;
      failure.details = body;
      throw failure;
    }
  };
}

// GET DELIST STATUS
export function getBulkDelistStatus (batchId) {
  return async () => {
    try {
      const response = await axios.get(endpoints.listing.delistStatus(batchId));
      if (response.data?.status === 'completed') {
        const { results } = response.data;
        trackEvent(ANALYTICS_EVENTS.LISTING_DELIST_COMPLETED, {
          success: results?.success ?? 0,
          partial: results?.partial ?? 0,
          failed: results?.failed ?? 0,
          skipped: results?.skipped ?? 0,
        });
      }
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      throw new Error(message);
    }
  };
}

// DELIST AND RELIST (queued - returns batchId)
// selectedAccounts (optional): [{ platform, integrationId? }] — when provided,
// only those platforms/accounts are delisted+relisted. Omit/null = all (back-compat).
export function delistAndRelist (listingIds, selectedAccounts) {
  return async (dispatch) => {
    try {
      const body = { ids: listingIds };
      if (Array.isArray(selectedAccounts) && selectedAccounts.length > 0) {
        body.selectedAccounts = selectedAccounts;
      }
      const response = await axios.post(endpoints.listing.delistRelist, body);
      trackEvent(ANALYTICS_EVENTS.LISTING_DELIST_RELIST_SUBMITTED, {
        count: Array.isArray(listingIds) ? listingIds.length : undefined,
      });
      // Returns { message, batchId, total }
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      throw new Error(message);
    }
  };
}

// GET DELIST-RELIST STATUS
export function getDelistRelistStatus (batchId) {
  return async () => {
    try {
      const response = await axios.get(`${endpoints.listing.delistRelist}/status/${batchId}`);
      if (response.data?.status === 'completed') {
        trackEvent(ANALYTICS_EVENTS.LISTING_DELIST_RELIST_COMPLETED, {
          success: response.data?.results?.success ?? 0,
          failed: response.data?.results?.failed ?? 0,
        });
      }
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      throw new Error(message);
    }
  };
}

// ----------------------------------------------------------------------
// MERGE LISTINGS FUNCTIONS
// ----------------------------------------------------------------------

/**
 * Preview merge result without executing
 * @param {string} primaryListingId - The listing to keep
 * @param {Array<string>} secondaryListingIds - Listings to preview merging
 */
export async function previewMerge (primaryListingId, secondaryListingIds) {
  try {
    const response = await axios.post(endpoints.listing.mergePreview, {
      primaryListingId,
      secondaryListingIds
    });
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

/**
 * Detect potential duplicate listings during import
 * @param {Array} listings - Listings being imported
 * @param {string} platform - Platform being imported from
 */
export async function detectDuplicates (listings, platform) {
  try {
    const response = await axios.post(endpoints.listing.detectDuplicates, {
      listings,
      platform
    });
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

/**
 * Merge imported listing data into an existing listing
 * @param {string} existingListingId - Existing listing to merge into
 * @param {Object} newPlatformData - New listing data from import
 * @param {string} platform - Platform being imported from
 */
export async function mergeImportedListing (existingListingId, newPlatformData, platform, integrationId = null) {
  try {
    const response = await axios.post(endpoints.listing.mergeImported, {
      existingListingId,
      newPlatformData,
      platform,
      integrationId
    });
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

// ----------------------------------------------------------------------
// BULK EDIT FUNCTIONS
// ----------------------------------------------------------------------

/**
 * Execute bulk edit for multiple listings
 * @param {string} mode - 'individual' or 'applyToAll'
 * @param {Array} updates - Array of update objects { id, ...fields } (for individual mode)
 * @param {Object} applyToAll - { ids: [], fields: {} } (for applyToAll mode)
 * @param {boolean} syncToPlatforms - Whether to sync changes to connected platforms
 * @param {Object} [howItWasSetUp] - What the seller used to build the edit: which fields they
 *   changed, which tools filled them, how many values they typed themselves, and how many
 *   listings were held back. Reported alongside the submission so the tools can be ranked by
 *   actual use; it is not sent to the server.
 */
export async function executeBulkEdit (mode, updates, applyToAll, syncToPlatforms = false, howItWasSetUp = {}) {
  try {
    const response = await axios.post(endpoints.listing.bulkEditExecute, {
      mode,
      updates,
      applyToAll,
      syncToPlatforms
    });
    const editCountSource = mode === 'applyToAll' ? applyToAll?.ids : updates;
    trackEvent(ANALYTICS_EVENTS.BULK_EDIT_SUBMITTED, {
      mode,
      count: Array.isArray(editCountSource) ? editCountSource.length : undefined,
      syncToPlatforms,
      ...howItWasSetUp,
    });
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

/**
 * Get bulk edit batch status
 * @param {string} batchId - Batch ID from executeBulkEdit
 */
export async function getBulkEditStatus (batchId) {
  try {
    const response = await axios.get(endpoints.listing.bulkEditStatus(batchId));
    if (response.data?.status === 'completed') {
      trackEvent(ANALYTICS_EVENTS.BULK_EDIT_COMPLETED, {
        success: response.data?.successCount ?? 0,
        failed: response.data?.failed ?? 0,
      });
    }
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

/**
 * How many of the seller's listings still have changes waiting for Chrome to send them to marketplaces.
 *
 * Throws when it can't be checked: a failed check is not "nothing waiting", and the bulk edit card
 * says the changes were sent when this answers 0.
 *
 * @returns {Promise<number>}
 */
export async function getPendingPlatformSyncsCount () {
  const response = await axios.get(endpoints.listing.pendingPlatformSyncs);
  return (response.data?.listings || []).length;
}

// ----------------------------------------------------------------------
// PULL FROM PLATFORM (per-account sync)
// ----------------------------------------------------------------------

/**
 * Server-side pull: fetch the live listing from a platform's OAuth API and
 * overwrite the corresponding ListingPlatformAccount row's per-account fields.
 *
 * For server-routed platforms only (eBay, Etsy, Shopify, BigCommerce, WooCommerce).
 * The Poshmark / Depop branches live in pullListingFromPlatform.
 *
 * On 410 PLATFORM_LISTING_NOT_FOUND the thrown error carries
 *   err.code === 'PLATFORM_LISTING_NOT_FOUND'
 * so the caller can prompt the user to deactivate the placement.
 *
 * On 409 INTEGRATION_DISCONNECTED the thrown error carries
 *   err.code === 'INTEGRATION_DISCONNECTED'
 */
export function pullListingFromPlatformServer (listingId, integrationId) {
  return async (dispatch) => {
    const integId = Number(integrationId);
    dispatch({ type: 'management/setAccountLoading', payload: { integrationId: integId, field: 'pulling', loading: true } });
    try {
      const url = endpoints.listing.pullFromPlatform(listingId, integId);
      const response = await axios.post(url);
      trackEvent(ANALYTICS_EVENTS.LISTING_PULL_FROM_PLATFORM_COMPLETED, {
        platform: platformOfAccount(response.data, integId),
      });
      dispatch(slice.actions.getListingSuccess(response.data));
      return response.data;
    } catch (error) {
      const data = error?.response?.data;
      const code = data?.code;
      const message = data?.message || error?.message || "Couldn't pull updates. Try again.";
      const wrapped = new Error(message);
      wrapped.code = code;
      wrapped.status = error?.response?.status;
      throw wrapped;
    } finally {
      dispatch({ type: 'management/setAccountLoading', payload: { integrationId: integId, field: 'pulling', loading: false } });
    }
  };
}

/**
 * Apply extension-fetched raw platform data to a ListingPlatformAccount row.
 * Used by the Poshmark / Depop branches of pullListingFromPlatform.
 */
export function applyExtensionFetchToAccount (listingId, integrationId, rawData) {
  return async (dispatch) => {
    const integId = Number(integrationId);
    dispatch({ type: 'management/setAccountLoading', payload: { integrationId: integId, field: 'pulling', loading: true } });
    try {
      const url = endpoints.listing.applyExtensionFetch(listingId, integId);
      const response = await axios.post(url, { rawData });
      dispatch(slice.actions.getListingSuccess(response.data));
      return response.data;
    } catch (error) {
      const data = error?.response?.data;
      const code = data?.code;
      const message = data?.message || error?.message || "Couldn't pull updates. Try again.";
      const wrapped = new Error(message);
      wrapped.code = code;
      wrapped.status = error?.response?.status;
      throw wrapped;
    } finally {
      dispatch({ type: 'management/setAccountLoading', payload: { integrationId: integId, field: 'pulling', loading: false } });
    }
  };
}

/**
 * Deactivate a single ListingPlatformAccount row (status='inactive') and
 * recompute listedOn. Used by the 404-confirm flow when the platform listing
 * has been deleted on the platform side.
 */
export function deactivatePlatformAccount (listingId, integrationId) {
  return async (dispatch) => {
    const integId = Number(integrationId);
    try {
      const url = endpoints.listing.deactivatePlatformAccount(listingId, integId);
      const response = await axios.post(url);
      trackEvent(ANALYTICS_EVENTS.LISTING_PLATFORM_ACCOUNT_DEACTIVATED, {
        platform: platformOfAccount(response.data, integId),
      });
      dispatch(slice.actions.getListingSuccess(response.data));
      // Dropping the last marketplace post leaves the listing inactive, freeing an active slot.
      dispatch(getUsage());
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Couldn't mark it as inactive. Try again.";
      throw new Error(message);
    }
  };
}

// ----------------------------------------------------------------------
// CSV IMPORT FUNCTIONS
// ----------------------------------------------------------------------

/**
 * Import listings from a CSV (parsed on client, sent as JSON)
 * @param {Array} listings - Array of mapped listing objects
 */
export async function csvImportListings (listings) {
  try {
    trackEvent(ANALYTICS_EVENTS.LISTING_CSV_IMPORT_STARTED, {
      count: Array.isArray(listings) ? listings.length : undefined,
    });
    const response = await axios.post(endpoints.listing.csvImport, { listings });
    trackEvent(ANALYTICS_EVENTS.LISTING_CSV_IMPORT_COMPLETED, {
      count: Array.isArray(listings) ? listings.length : undefined,
    });
    return response.data;
  } catch (error) {
    trackEvent(ANALYTICS_EVENTS.LISTING_CSV_IMPORT_FAILED, {
      count: Array.isArray(listings) ? listings.length : undefined,
      error: error?.response?.data?.message || error?.message,
    });
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

/**
 * Download the Voolist CSV template
 */
export async function downloadCsvTemplate () {
  try {
    const response = await axios.get(endpoints.listing.csvTemplate, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'voolist-csv-template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

// ----------------------------------------------------------------------
// INKFROG IMPORT FUNCTIONS
// ----------------------------------------------------------------------

/**
 * Start an Inkfrog CSV import by uploading the file
 * @param {File} file - CSV File object
 * @returns {Promise<{ success: boolean, jobId: string }>}
 */
export async function startInkfrogImport (file) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(endpoints.listing.inkfrogImport, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

/**
 * Poll the status of an Inkfrog import job
 * @param {string} jobId - Job ID returned from startInkfrogImport
 * @returns {Promise<object>} Job status
 */
export async function getInkfrogImportStatus (jobId) {
  try {
    const response = await axios.get(endpoints.listing.inkfrogImportStatus(jobId));
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

export async function confirmInkfrogImport (jobId, decisions, selectedNoDuplicates) {
  const response = await axios.post(endpoints.listing.inkfrogImportConfirm(jobId), {
    decisions,
    selectedNoDuplicates,
  });
  return response.data;
}

// ----------------------------------------------------------------------
// BULK MERGE FUNCTIONS
// ----------------------------------------------------------------------

/**
 * Detect merge groups among selected listings (each group can hold more than one duplicate)
 * @param {Array<string>|null} listingIds - Specific listing IDs, or null for all listings
 */
export async function detectMergeGroupsApi (listingIds) {
  try {
    const response = await axios.post(endpoints.listing.bulkMergeDetectGroups, {
      listingIds
    });
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

/**
 * Execute a bulk merge run for multiple groups
 * @param {Array<Object>} groups
 */
export async function executeMergeGroupsApi (groups) {
  const response = await axios.post(endpoints.listing.bulkMergeExecute, {
    groups
  });
  return response.data;
}

/**
 * Get merge run (batch) status
 * @param {string} batchId - Batch ID from executeMergeGroupsApi
 */
export async function getMergeRunStatusApi (batchId) {
  try {
    const response = await axios.get(endpoints.listing.bulkMergeStatus(batchId));
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message);
  }
}

